<template>
    <div>
        <div class="d-flex">
            <div class="flex-grow-1">
                <div class="d-flex align-items-center">
                    <label
                        for="year"
                        class="mr-2"
                    >Year:</label>
                    <select
                        class="form-control mb-2"
                        v-model="year"
                        id="year"
                    >
                        <option
                            v-for="y in years"
                            :key="y"
                            :value="y"
                        >
                            {{ y }}{{ y === currentYear ? ' (current)' : '' }}
                        </option>
                    </select>
                </div>
                <table class="table bg-white">
                    <tr>
                        <td>{{ sickDaysTaken }}</td>
                        <td class="bold">
                            Sick Days Taken
                        </td>
                    </tr>
                    <tr>
                        <td>{{ bankHolidaysTotal }}</td>
                        <td class="bold">
                            Bank Holidays
                        </td>
                    </tr>
                    <tr>
                        <td>{{ holidayDaysTaken }}</td>
                        <td class="bold">
                            Holiday Days Taken
                        </td>
                    </tr>
                    <tr>
                        <td>{{ allowance - holidayDaysTaken }}</td>
                        <td class="bold">
                            Holiday Days Remaining
                        </td>
                    </tr>
                    <tr>
                        <td>{{ companyClosedTotal }}</td>
                        <td class="bold">
                            Company Closed Days
                        </td>
                    </tr>
                </table>
            </div>
        </div>
        <div class="options-row mb-2 justify-content-between align-items-center py-2">
            <button
                class="btn btn-success"
                type="button"
                @click="newEvent"
            >
                + Add Event
            </button>
            <div class="options-row ml-auto">
                <label class="d-block mr-2">
                    <input
                        type="checkbox"
                        v-model="showBankHolidays"
                        class="align-middle"
                    >
                    <span>Show Bank Holidays</span>
                </label>
                <label class="d-block mr-2">
                    <input
                        type="checkbox"
                        v-model="showDeclined"
                        class="align-middle"
                    >
                    <span>Show Declined</span>
                </label>
                <label class="d-block mr-2">
                    <input
                        type="checkbox"
                        v-model="showApproved"
                        class="align-middle"
                    >
                    <span>Show Approved</span>
                </label>
                <label class="d-block">
                    <input
                        type="checkbox"
                        v-model="showPast"
                        class="align-middle"
                    >
                    <span>Show Past</span>
                </label>
            </div>
        </div>
        <p v-if="loading">
            Loading...
        </p>
        <p v-if="!loading && filteredEvents.length === 0">
            No events for this user
        </p>
        <event-item
            v-for="event in filteredEvents"
            :key="event.id"
            :event="event"
            @edit="(e) => {openEvent = e}"
        />
        <form-modal
            v-if="openEvent"
            :event="openEvent"
            @saved="onSaved"
        />
    </div>
</template>

<script>
import moment from "moment";
import EventItem from "./UserEventItem.vue";
import FormModal from "../../calendar/FormModal.vue";
import UsersService from "../../../../services/UsersService";
import AuthService from "../../../../services/AuthService";
import {calculateDaysDiff} from "../../../../include/Calendar";

export default {
    components: {
        EventItem,
        FormModal,
    },
    props: {
        userId: {
            type: Number,
            required: true,
        },
    },
    data() {
        return {
            loading: true,
            events: [],
            openEvent: null,
            allowance: null,
            showBankHolidays: false,
            showDeclined: false,
            showApproved: false,
            showPast: false,
            year: moment().year(),
            currentYear: moment().year(),
        };
    },
    computed: {
        filteredEvents() {
            return this.events.filter((e) => {
                if (!this.showPast && e.start_at.isBefore(moment())) {
                    return false;
                }

                if (e.type === "Bank Holiday") {
                    return this.showBankHolidays;
                }
                if (!this.showDeclined && e.status === "Declined") {
                    return false;
                }
                if (!this.showApproved && e.status === "Approved") {
                    return false;
                }

                return true;
            });
        },
        holidayDaysTaken() {
            return this.eventsTotalLength(this.events.filter(e => e.status === 'Approved' && e.type === 'Annual Leave'));
        },
        sickDaysTaken() {
            return this.eventsTotalLength(this.events.filter(e => e.status === 'Approved' && e.type === 'Sickness'));
        },
        bankHolidaysTotal() {
            return this.eventsTotalLength(this.events.filter(e => e.type === 'Bank Holiday'));
        },
        companyClosedTotal() {
            return this.eventsTotalLength(this.events.filter(e => e.type === 'Company Closed'));
        },
        years() {
            const years = [];
            const minYear = moment().year() - 5;
            const maxYear = moment().year() + 2;
            for (let i = minYear; i <= maxYear; i++) {
                years.push(i);
            }
            return years;
        },
        startDate() {
            const setting = window.SystemSettings.annual_leave_start_date || 'XXXX-01-01';

            const start = `${this.year}${setting.substring(4)}`;

            return start.substring(0, 10)
        },
        endDate() {
            return moment(this.startDate).add(1, 'year').subtract(1, 'day').format('YYYY-MM-DD');
        }
    },
    watch: {
        year() {
            this.fetchAll();
            if (this.year < moment().year()) {
                this.showPast = true;
            } else {
                this.showPast = false;
            }
        }
    },
    mounted() {
        this.showBankHolidays = AuthService.user.id === this.userId;
        this.showApproved = AuthService.user.id === this.userId;
        this.fetchAll();
    },
    methods: {
        fetchAll() {
            this.fetchEvents();
            this.fetchAllowance();
        },
        fetchEvents() {
            this.loading = true;
            window.axios
                .get("/api/calendar-events", {
                    params: {
                        user_id: this.userId,
                        sort_dir: "asc",
                        start: this.startDate,
                        end: this.endDate,
                    },
                })
                .then(({data}) => {
                    this.loading = false;
                    this.events = data.filter((event) => {
                        return event.type !== 'Full Day'
                            && event.type !== 'Early Start'
                            && event.type !== 'Overnight';
                    }).map((date) => ({
                        ...date,
                        start_at: moment(date.start_at),
                        end_at: moment(date.end_at),
                        days: this.eventLength(data),
                    }));
                });
        },
        fetchAllowance() {
            UsersService.leaveAllowance(this.userId).then(({data}) => {
                this.allowance = data.allowance;
            });
        },
        onSaved(event) {
            this.openEvent = null;
            this.fetchAll();
        },
        newEvent() {
            const start = moment().startOf("day");
            this.openEvent = {
                type: "Annual Leave",
                start_at: start,
                end_at: start.clone().endOf("day"),
                user_id: this.userId,
                status: "Pending",
            };
        },
        eventLength(event) {
            return calculateDaysDiff(moment(event.start_at), moment(event.end_at));
        },
        eventsTotalLength(events) {
            let total = 0;
            events.forEach(event => {
                total += this.eventLength(event);
            })

            return total;
        }
    },
};
</script>
<style scoped>
.options-row {
    display: flex;
}
@media (max-width: 530px) {
    .options-row {
        display: block;
        text-align: right;
    }
}
</style>


// WEBPACK FOOTER //
// resources/assets/js/components/office/admin/users/UserEventsList.vue